/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Deal } from '@/services/requests/housien-services/DealsService';
import { lastPauseEvent } from './deal-events';
import { resolveNumberLocale } from '@/shared/utils/number-format';
import { ACTIVE_PIPELINE_STAGES, type ActivePipelineStage } from '../constants/deal-stages';

/** The three lifecycle families the detail screen renders differently. */
export type DealLifecycle = 'running' | 'paused' | 'terminal';

/**
 * Accent family the screen keys its stage color on. Early funnel and the
 * paused state read mostarda, proposal onward reads petróleo, and the two
 * outcomes read sálvia (won) / cobre (lost).
 */
export type DealAccent = 'mostarda' | 'petroleo' | 'salvia' | 'cobre';

export interface DealViewState {
  lifecycle: DealLifecycle;
  /**
   * Index in the 5-step active pipeline; while paused it is the position
   * the deal returns to on resume, and -1 once terminal.
   */
  stageIndex: number;
  /** The stage shown in the summary card: the held stage while paused. */
  displayStage: string;
  isWon: boolean;
  isLost: boolean;
  /**
   * Sitting on the last running stage.
   *
   * This used to be `stage === 'closing'`. `closing` left the pipeline —
   * the paperwork it stood for belongs to the Sale or the Lease, not to
   * the attendance — so the flag now means what it always read as: the
   * far end of the funnel, where a win is possible.
   */
  isAtLastStage: boolean;
  accent: DealAccent;
}

/**
 * Derives the lifecycle family of a deal's stage.
 */
function lifecycleOf(stage: string): DealLifecycle {
  if (stage === 'paused') return 'paused';
  if (stage === 'won' || stage === 'lost') return 'terminal';
  return 'running';
}

/**
 * Picks the accent family for a lifecycle + pipeline position.
 */
function accentOf(lifecycle: DealLifecycle, isWon: boolean, stageIndex: number): DealAccent {
  if (lifecycle === 'terminal') return isWon ? 'salvia' : 'cobre';
  if (lifecycle === 'paused') return 'mostarda';
  // Only the last column reads as the late funnel. `proposal` and
  // `negotiation` both wore petroleo and collapsed into `negotiating`;
  // `visiting` wore mostarda and still does.
  return stageIndex >= ACTIVE_PIPELINE_STAGES.length - 1 ? 'petroleo' : 'mostarda';
}

/**
 * The accent family a bare stage reads as, with no deal around it.
 *
 * The board's cards need the same accent the detail screen will show
 * once opened, and they hold a stage rather than a whole deal. Derived
 * from the same rule so the two screens cannot disagree on the colour of
 * a stage.
 *
 * @param stage - The attendance's current stage.
 * @returns The accent family for that stage.
 */
export function dealAccentForStage(stage: string): DealAccent {
  const lifecycle = lifecycleOf(stage);
  const stageIndex = ACTIVE_PIPELINE_STAGES.indexOf(stage as ActivePipelineStage);
  return accentOf(lifecycle, stage === 'won', stageIndex);
}

/**
 * Derives everything the detail screen branches on from the deal's stage.
 *
 * @param deal - The deal being displayed.
 * @returns The lifecycle, pipeline position, and accent family.
 */
export function dealViewState(deal: Deal): DealViewState {
  const lifecycle = lifecycleOf(deal.stage);
  // While paused, show the stage it was parked from. That used to be
  // the `previous_stage` field; the field is gone, and the pause entry
  // on the timeline carries the same answer.
  const displayStage = lifecycle === 'paused'
    ? lastPauseEvent(deal.events)?.from ?? deal.stage
    : deal.stage;
  const stageIndex = ACTIVE_PIPELINE_STAGES.indexOf(displayStage as ActivePipelineStage);
  const isWon = deal.stage === 'won';
  return {
    lifecycle,
    stageIndex,
    displayStage,
    isWon,
    isLost: deal.stage === 'lost',
    isAtLastStage: stageIndex === ACTIVE_PIPELINE_STAGES.length - 1,
    accent: accentOf(lifecycle, isWon, stageIndex),
  };
}

/** Text-color utility class per accent family. */
export const ACCENT_TEXT_CLASS: Record<DealAccent, string> = {
  mostarda: 'text-(--accent-mostarda)',
  petroleo: 'text-(--accent-petroleo)',
  salvia: 'text-(--accent-salvia)',
  cobre: 'text-(--accent-cobre)',
};

/** Solid background utility class per accent family. */
export const ACCENT_BG_CLASS: Record<DealAccent, string> = {
  mostarda: 'bg-(--accent-mostarda)',
  petroleo: 'bg-(--accent-petroleo)',
  salvia: 'bg-(--accent-salvia)',
  cobre: 'bg-(--accent-cobre)',
};

/**
 * Soft (14%) background utility class per accent family — the tint every
 * chip and attribution badge on these screens sits on.
 */
export const ACCENT_SOFT_BG_CLASS: Record<DealAccent, string> = {
  mostarda: 'bg-(--accent-mostarda)/14',
  petroleo: 'bg-(--accent-petroleo)/14',
  salvia: 'bg-(--accent-salvia)/14',
  cobre: 'bg-(--accent-cobre)/14',
};

/**
 * 1.5px halo per accent family, for the timeline dots.
 *
 * The dot needs a ring rather than a fill: it sits on the connecting
 * line, and a plain dot reads as a bead threaded on it instead of a
 * marker punched through it. Drawn as a shadow so it costs no layout.
 */
export const ACCENT_RING_CLASS: Record<DealAccent, string> = {
  mostarda: 'shadow-[0_0_0_1.5px_var(--accent-mostarda)]',
  petroleo: 'shadow-[0_0_0_1.5px_var(--accent-petroleo)]',
  salvia: 'shadow-[0_0_0_1.5px_var(--accent-salvia)]',
  cobre: 'shadow-[0_0_0_1.5px_var(--accent-cobre)]',
};

/**
 * Faded (45%) background utility class per accent, for passed steps.
 *
 * Mixed towards `--border-subtle` rather than set at 45% alpha: the
 * strip's unreached steps are painted in that border tone, so a passed
 * step has to land between the accent and *it* to read as the same
 * strip. Blending towards transparent instead let the page ground show
 * through and the passed steps drifted off the scale.
 */
export const ACCENT_FADED_BG_CLASS: Record<DealAccent, string> = {
  mostarda: 'bg-[color-mix(in_srgb,var(--accent-mostarda)_45%,var(--border-subtle))]',
  petroleo: 'bg-[color-mix(in_srgb,var(--accent-petroleo)_45%,var(--border-subtle))]',
  salvia: 'bg-[color-mix(in_srgb,var(--accent-salvia)_45%,var(--border-subtle))]',
  cobre: 'bg-[color-mix(in_srgb,var(--accent-cobre)_45%,var(--border-subtle))]',
};

/** Full-opacity border utility class per accent, for active tab underlines. */
export const ACCENT_UNDERLINE_CLASS: Record<DealAccent, string> = {
  mostarda: 'border-(--accent-mostarda)',
  petroleo: 'border-(--accent-petroleo)',
  salvia: 'border-(--accent-salvia)',
  cobre: 'border-(--accent-cobre)',
};

/** Border utility class per accent family, for tinted containers. */
export const ACCENT_BORDER_CLASS: Record<DealAccent, string> = {
  mostarda: 'border-(--accent-mostarda)/40',
  petroleo: 'border-(--accent-petroleo)/40',
  salvia: 'border-(--accent-salvia)/40',
  cobre: 'border-(--accent-cobre)/40',
};

/**
 * Formats a stored money amount as BRL in the active i18n locale.
 *
 * @param value - The stored amount, if any.
 * @returns The formatted currency, or null when there is no amount.
 */
export function formatDealMoney(value: number | null | undefined): string | null {
  if (value == null) return null;
  return new Intl.NumberFormat(resolveNumberLocale(), {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Whole days elapsed since the given timestamp.
 *
 * @param iso - The reference timestamp.
 * @returns The day count, or null when the timestamp does not parse.
 */
export function daysSince(iso: string | undefined): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

/**
 * How long an attendance may sit still before the board calls it stalled.
 *
 * A week, because that is the span after which a broker reading the
 * board would agree nothing has happened — shorter and every card over a
 * weekend shouts, longer and a fortnight of silence still reads calm.
 *
 * It lives here rather than in the card so the board and anything else
 * that judges staleness cannot drift to different weeks.
 */
export const STALE_AFTER_DAYS = 7;

/** How a card should paint its idle line. */
export type IdleTone = 'fresh' | 'quiet' | 'stalled';

/**
 * The tone for an attendance that last moved `days` ago.
 *
 * @param days - Whole days since the last recorded fact, or null when
 *   nothing dates the attendance.
 * @returns The tone, or null when there is nothing to say.
 */
export function idleToneFor(days: number | null): IdleTone | null {
  if (days == null) return null;
  if (days === 0) return 'fresh';
  return days >= STALE_AFTER_DAYS ? 'stalled' : 'quiet';
}

/** The token each idle tone paints its text in. */
export const IDLE_TONE_COLOR: Record<IdleTone, string> = {
  fresh: 'var(--status-success-text)',
  quiet: 'var(--text-tertiary-soft)',
  stalled: 'var(--accent-cobre)',
};

/**
 * The value of the offer that won the attendance, if one did.
 *
 * The attendance used to keep an `accepted_value` of its own. It does
 * not any more: the value belongs to the Sale or the Lease the
 * acceptance opens, and the number itself is right here on the offer
 * that was accepted — no second copy to disagree with.
 */
export function acceptedOfferValue(deal: Deal): number | undefined {
  return deal.proposals?.find((p) => p.status === 'accepted')?.value;
}
